import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getAppPermissions } from 'src/common/NavMenuLink/permissionsFunctions';
import { useModal } from 'src/state/modal';
import { NavMenuPrimitives } from '@uhg-abyss/web/ui/NavMenu';
import { IconMaterial } from '@uhg-abyss/web/ui/IconMaterial';
import { useUser } from '@sde/widgets-core/src/redux/resources/user';
import { getUserPermissions } from '@sde/widgets-core/src/redux/resources/user/helpers';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Text } from '@uhg-abyss/web/ui/Text';
import { getFeature } from '@sde/widgets-core/src/redux/tools/features';
import { useProvider } from 'src/state/provider';
import { useOverlay } from '@uhg-abyss/web/hooks/useOverlay';
import { usePageCollapsingHeader } from 'src/state/pageCollapsingHeader';
import {
  handleLinkClickFunction,
  getAppLinkHandler,
} from '@sde/widgets-core/src/tools/linkHelperFunctions';
import { handleLinkClickFunctionForSubMenuDropdownContent } from '@sde/widgets-core/src/tools/linkHelperFunctions/linkHelperFunctions';

const NewTagContainer = styled('div', {
  display: 'inline-flex',
  gap: '5px',
  alignItems: 'center',
});

const NewIndicatorTag = styled(Text, {
  alignSelf: 'baseline',
});

const ExternalIconContainer = styled('div', {
  display: 'inline-flex',
});

export const NavMenuLinkNew = ({
  name,
  description,
  href,
  queryParam,
  statusToggles,
  permission,
  hideIfUserNotPermitted,
  dataTestId,
  openInNewWindow,
  useAPIRedirect,
  newIndicatorFlag,
  isInternal,
}) => {
  const { provider } = useProvider.state();
  const { user } = useUser.state();
  const { isSuperUser, isUhcInsightsUser } = user;
  const { toggleModal } = useModal.actions();
  const [appDSPermissions, setAppDSPermissions] = useState({});
  const newIndicatorText = 'NEW';
  const flyout = useOverlay('pageheader-model');
  const { isSubMenuDropdownContent } = usePageCollapsingHeader.state();
  useEffect(() => {
    if (user && user.permissions) {
      const { providerOrg, nonProviderOrg } = user;
      const isNonProviderOrgOnlyUser =
        providerOrg?.length < 1 && nonProviderOrg?.length > 0;
      if (isNonProviderOrgOnlyUser) {
        setAppDSPermissions(getUserPermissions(user));
      } else if (
        provider.taxIdNumber &&
        provider.corporateProviderMpin &&
        provider.userId
      ) {
        setAppDSPermissions(getUserPermissions(user, provider));
      }
    }
  }, [
    user,
    user.permissions,
    provider.taxIdNumber,
    provider.corporateProviderMpin,
    provider.userId,
  ]);
  const hasPermission = getAppPermissions(permission, appDSPermissions, name);
  if (hideIfUserNotPermitted && !hasPermission) return null;

  if (
    statusToggles &&
    !statusToggles.split(',').some((toggle) => getFeature(toggle))
  )
    return null;

  const queryParamData = {
    ...(user && { uuid: user.uuid }),
  };

  const redirect = getAppLinkHandler(href, queryParam, queryParamData);

  const formatTitle = () => {
    const hasNewIndicator = getFeature(newIndicatorFlag) || false;
    if (openInNewWindow || useAPIRedirect || !href.includes('#/')) {
      return (
        <NewTagContainer>
          {name}
          <ExternalIconContainer>
            <IconMaterial icon="launch" size="16px" />
          </ExternalIconContainer>
          <If condition={hasNewIndicator}>
            <NewIndicatorTag color="$error1" fontWeight="bold" size={12}>
              {newIndicatorText}
            </NewIndicatorTag>
          </If>
        </NewTagContainer>
      );
    }
    return (
      <React.Fragment>
        <Choose>
          <When condition={hasNewIndicator}>
            <NewTagContainer>
              {name}{' '}
              <NewIndicatorTag color="$error1" fontWeight="bold" size={12}>
                {newIndicatorText}
              </NewIndicatorTag>
            </NewTagContainer>
          </When>
          <Otherwise>{name}</Otherwise>
        </Choose>
      </React.Fragment>
    );
  };

  let updatedOpenInNewWindow = openInNewWindow ? true : null;

  if (useAPIRedirect || (!redirect.includes('#') && isSuperUser))
    updatedOpenInNewWindow = false;

  return (
    <div data-testid={dataTestId}>
      <NavMenuPrimitives.MenuItem
        css={{
          alignItems: 'flex-start',
          justifyContent: 'center',
          padding: '4px 8px !important',
          '&:hover': {
            backgroundColor: '$gray2',
          },
        }}
        title={formatTitle()}
        description={description}
        onClick={() =>
          isSubMenuDropdownContent
            ? handleLinkClickFunctionForSubMenuDropdownContent({
                hasPermission,
                toggleModal,
                name,
                href,
                redirect,
                isSuperUser,
                isUhcInsightsUser,
                isInternal,
                componentName: 'NavMenuLink',
                flyout,
              })
            : handleLinkClickFunction({
                hasPermission,
                toggleModal,
                name,
                href,
                redirect,
                isSuperUser,
                isUhcInsightsUser,
                isInternal,
                componentName: 'NavMenuLink',
              })
        }
        openNewWindow={updatedOpenInNewWindow}
        hideIcon
      />
    </div>
  );
};

NavMenuLinkNew.propTypes = {
  name: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  href: PropTypes.string.isRequired,
  queryParam: PropTypes.string,
  statusToggles: PropTypes.string,
  permission: PropTypes.string,
  hideIfUserNotPermitted: PropTypes.bool,
  dataTestId: PropTypes.string,
  openInNewWindow: PropTypes.bool,
  useAPIRedirect: PropTypes.bool,
  newIndicatorFlag: PropTypes.bool,
  isInternal: PropTypes.bool,
};

NavMenuLinkNew.defaultProps = {
  queryParam: null,
  statusToggles: '',
  permission: '',
  hideIfUserNotPermitted: false,
  dataTestId: null,
  openInNewWindow: false,
  useAPIRedirect: false,
  newIndicatorFlag: false,
  isInternal: false,
};
